const normalizeBaseUrl = (baseUrl: string): string => {
  const withLeadingSlash = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
  return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
};

export const stripApplicationBase = (
  pathname: string,
  baseUrl = import.meta.env.BASE_URL,
): string => {
  const base = normalizeBaseUrl(baseUrl);
  if (base === '/') return pathname;

  const baseWithoutSlash = base.slice(0, -1);
  if (pathname === baseWithoutSlash) return '/';
  if (!pathname.startsWith(base)) return pathname;
  return `/${pathname.slice(base.length)}`;
};

export const rebaseRootRelativeUrl = (
  value: string,
  baseUrl = import.meta.env.BASE_URL,
): string => {
  const base = normalizeBaseUrl(baseUrl);
  if (
    base === '/'
    || !value.startsWith('/')
    || value.startsWith('//')
    || value === base.slice(0, -1)
    || value.startsWith(base)
  ) return value;

  return `${base.slice(0, -1)}${value}`;
};

const ROOT_URL_ATTRIBUTES = ['href', 'src', 'poster'] as const;

const rebaseElement = (element: Element, baseUrl: string): void => {
  for (const attribute of ROOT_URL_ATTRIBUTES) {
    const value = element.getAttribute(attribute);
    if (value === null) continue;
    const rebased = rebaseRootRelativeUrl(value, baseUrl);
    if (rebased !== value) element.setAttribute(attribute, rebased);
  }
};

const rebaseTree = (node: Node, baseUrl: string): void => {
  if (!(node instanceof Element)) return;
  rebaseElement(node, baseUrl);
  for (const element of node.querySelectorAll('[href], [src], [poster]')) {
    rebaseElement(element, baseUrl);
  }
};

export const installApplicationBaseUrl = (
  root: Element,
  baseUrl = import.meta.env.BASE_URL,
): (() => void) => {
  const base = normalizeBaseUrl(baseUrl);
  if (base === '/') return () => undefined;

  rebaseTree(root, base);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        rebaseTree(record.target, base);
        continue;
      }
      for (const node of record.addedNodes) rebaseTree(node, base);
    }
  });
  observer.observe(root, {
    attributeFilter: [...ROOT_URL_ATTRIBUTES],
    attributes: true,
    childList: true,
    subtree: true,
  });
  return () => observer.disconnect();
};
